import React, { useRef, useState } from 'react';
import s from './s.module.less';
import { Prospect } from 'types/common';
import { Spin, message } from 'antd';
import { EAIEditorType } from 'types/ai';
import { TWebSocketListener, useWebSocket } from 'hooks/inbox/useWebSocket';
import { EWSAction, TServerMessage, TWSMessage, TConversation, EMessageSenderType } from 'types/inbox';
import { handleServerSideMessage, unitAndSortServerMessages } from 'utils/inbox';
import useConversationInfo from 'hooks/useConversationInfo';
import { getUserId } from 'utils/localstore';
import { getConversationMessageList, sendMessage } from 'api/inbox';
import { gpGetPatientConversations, gpGetMessages, gpSendMessage, gpMarkRead } from 'api/group';
import InboxMessageList from 'pages/dashboard/InboxPage/MessageContainer/components/InboxMessageList';
import ChatInput, { ChatInputValue } from 'components/ChatInput';
import { ClockCircleOutlined } from '@ant-design/icons';
import { getTimeStr } from 'utils/calendar';
import dayjs from 'dayjs';
import { ComposerDraft, resolveComposerDraft } from './composerDraft';

type Props = {
    prospect?: Prospect;
    hasBanner: boolean;
    homeInfo?: unknown;
    isOps?: boolean;
    isGroupPractice?: boolean;
    /** One request to prefill the composer. Fills the box only; the provider still clicks send. */
    composerDraft?: ComposerDraft;
    onComposerDraftHandled?: () => void;
};
const DEFAULT_FETCH_MESSAGE_COUNT = 30;
const LOAD_HISTORY_INTERVAL = 1000 * 2;

const AVATAR_COLORS = ['#0E62F4', '#059669', '#D97706', '#1E40AF', '#065F46', '#92400E'];

const getAvatarColor = (name: string): string => {
    const code = name.charCodeAt(0) || 0;
    return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

const getInitials = (firstName?: string, lastName?: string): string =>
    `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();

const ClientMessage = (props: Props) => {
    const { prospect, hasBanner, isGroupPractice, composerDraft, onComposerDraftHandled } = props;
    const canSend = true;

    const isSample = prospect?.isSample;

    const [conversation] = useConversationInfo(
        isGroupPractice ? undefined : getUserId(),
        isGroupPractice ? undefined : prospect?.id!,
    );

    // GP conversation list state
    const [gpConversations, setGpConversations] = useState<TConversation[]>([]);
    const [gpConversationsLoading, setGpConversationsLoading] = useState(false);
    const [gpSelectedConversationId, setGpSelectedConversationId] = useState<string>();

    const conversationMessageMap = React.useRef<Record<string, TServerMessage[] | undefined>>({});
    const selectConversationIdRef = React.useRef<string | undefined>();
    const [messageList, setMessageList] = useState<TServerMessage[]>();
    const [replayContentValue, setReplayContentValue] = useState<ChatInputValue>({ text: '', files: [] });
    const [, setResetFlag] = useState<{}>({});
    const [loadingConversationMessage, setLoadingConversationMessage] = useState<boolean>(false);
    const latestConversationMessageCursor = React.useRef<Record<string, string>>({});
    const latestLoadHistoryTime = React.useRef<number>(new Date().getTime());
    const allConversationMessageFetchedFlag = React.useRef<Record<string, boolean>>({});
    const [replyingMsg, setReplyingMsg] = useState<boolean>(false); //main pannel
    const providerId = getUserId();
    const loadingHistoryMessageRef = useRef<boolean>(false);
    const [, forceUpdate] = useState<{}>();
    const autoSuggestionRef = useRef<string>();
    const composerDraftActiveRef = useRef<boolean>(false);

    const scrollToLatestMessage = (timeout?: number, smooth?: boolean) => {
        setTimeout(() => {
            const messageWrap = document.getElementById('clientMessageWrap');
            if (messageWrap) {
                messageWrap.scrollTo({
                    top: 99999,
                    behavior: smooth ? 'smooth' : 'auto',
                });
            }
        }, timeout || 150);
    };

    const socketListeners = React.useRef<TWebSocketListener>({
        onOpen: (event) => {
            console.log('WebSocket connection opened:', event);
        },
        onClose: (event) => {
            console.warn('WebSocket connection closed:', event);
        },
        onMessage: (event) => {
            console.info('WebSocket onMessage:', event);
            const msgData = event.data;
            try {
                const wsMsg: TWSMessage = JSON.parse(msgData);
                if (wsMsg.action === EWSAction.NEWMSG) {
                    const serverMsg = wsMsg.payload as TServerMessage;
                    const conversationIdFromWs = serverMsg.conversationId;
                    const cacheMessageList = conversationMessageMap.current[conversationIdFromWs] || [];
                    let _message = unitAndSortServerMessages([...(cacheMessageList || []), serverMsg]);

                    const currentSortId = serverMsg.sortKey;
                    const targetIndex = cacheMessageList.findIndex((item) => item.sortKey === currentSortId);
                    if (targetIndex >= 0) {
                        cacheMessageList[targetIndex] = { ...serverMsg };
                        _message = unitAndSortServerMessages(cacheMessageList);
                    }

                    conversationMessageMap.current[conversationIdFromWs] = _message || [];
                    if (selectConversationIdRef.current === conversationIdFromWs) {
                        setMessageList(_message);
                        scrollToLatestMessage(undefined, true);
                    }

                    if (isGroupPractice) {
                        fetchGPConversations(); // eslint-disable-line @typescript-eslint/no-use-before-define
                    }
                } else {
                    console.warn('Unknown WebSocket action:', msgData);
                }
            } catch (e) {
                console.error(e);
            }
        },
        onError: (event) => {
            console.error('WebSocket error:', event);
        },
        autoCloseTime: 1000 * 60 * 10,
        autoReconnectTime: 1000 * 20,
    });
    useWebSocket(socketListeners.current);

    // Fetch GP conversations
    const fetchGPConversations = React.useCallback(async () => {
        if (!prospect?.identityId) return;
        setGpConversationsLoading(true);
        try {
            const res = await gpGetPatientConversations(prospect.identityId);
            if (!res.error) {
                const list = res?.data?.data || [];
                setGpConversations(list);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setGpConversationsLoading(false);
        }
    }, [prospect?.identityId]);

    React.useEffect(() => {
        if (isGroupPractice && prospect?.identityId) {
            fetchGPConversations();
        }
    }, [isGroupPractice, prospect?.identityId, fetchGPConversations]);

    // Auto-select first conversation when GP conversations load
    React.useEffect(() => {
        if (isGroupPractice && gpConversations.length > 0 && !gpSelectedConversationId) {
            const firstId = gpConversations[0].conversationId;
            setGpSelectedConversationId(firstId);
            selectConversationIdRef.current = firstId;
            fetchMessageByConversationId(firstId); // eslint-disable-line @typescript-eslint/no-use-before-define
        }
    }, [isGroupPractice, gpConversations, gpSelectedConversationId]);

    const fetchMessageByConversationId = async (conversationId: string, cursor?: string, size?: number) => {
        const fetchSize = size || DEFAULT_FETCH_MESSAGE_COUNT;

        setLoadingConversationMessage(true);
        const res = isGroupPractice
            ? await gpGetMessages({ conversationId, size: fetchSize, cursor, identityId: prospect?.identityId })
            : await getConversationMessageList({ conversationId, size: fetchSize, cursor });

        if (!res.error) {
            const data = res?.data?.data?.items || [];
            const isEmpty = data.length === 0;
            const _cursor = res?.data?.data?.cursor;
            const currentCursor = latestConversationMessageCursor.current[conversationId] || '';
            if (!currentCursor || cursor) {
                latestConversationMessageCursor.current[conversationId] = _cursor || '';
            }

            if (isEmpty) {
                allConversationMessageFetchedFlag.current[conversationId] = true;
            }

            const cacheMessageList = conversationMessageMap.current[conversationId] || [];
            const _message = unitAndSortServerMessages([...(cacheMessageList || []), ...data]);
            conversationMessageMap.current[conversationId] = _message || [];
            const aiReplyContent = _message?.[_message.length - 1]?.aiReplyContent;
            if (aiReplyContent && !composerDraftActiveRef.current) {
                autoSuggestionRef.current = aiReplyContent;
                setReplayContentValue({ text: aiReplyContent, files: [] });
            }
            setMessageList(_message);
            if (!cursor) {
                scrollToLatestMessage();
            }
        }
        setLoadingConversationMessage(false);
    };

    const loadHistoryMessage = async () => {
        if (loadingHistoryMessageRef.current === true) {
            return;
        }
        const targetConversationId = selectConversationIdRef.current;
        if (!targetConversationId) {
            return;
        }
        const cursor = latestConversationMessageCursor.current[targetConversationId] || '';
        if (!cursor) {
            return;
        }

        const allLoad = allConversationMessageFetchedFlag.current[targetConversationId];
        if (allLoad) {
            return;
        }
        const nowTime = new Date().getTime();
        const latestLoadTime = latestLoadHistoryTime.current;
        const diff = nowTime - latestLoadTime;
        if (diff < LOAD_HISTORY_INTERVAL) {
            return;
        }
        latestLoadHistoryTime.current = nowTime;
        loadingHistoryMessageRef.current = true;
        forceUpdate({});
        await fetchMessageByConversationId(targetConversationId, cursor, DEFAULT_FETCH_MESSAGE_COUNT);
        loadingHistoryMessageRef.current = false;
        forceUpdate({});
    };

    // Non-GP: init from useConversationInfo
    React.useEffect(() => {
        if (isGroupPractice) return;
        if (!conversation || !conversation.conversationId) {
            return;
        }
        selectConversationIdRef.current = conversation.conversationId;
        fetchMessageByConversationId(conversation.conversationId);
        const messageWrap = document.getElementById('clientMessageWrap');
        const messageScrollCheck = (e: Event) => {
            const target = e.target as HTMLElement;
            const { scrollTop } = target;
            if (scrollTop === 0) {
                loadHistoryMessage();
            }
        };
        if (messageWrap) {
            messageWrap.addEventListener('scroll', messageScrollCheck);
        }

        return () => {
            if (messageWrap) {
                messageWrap.removeEventListener('scroll', messageScrollCheck);
            }
        };
    }, [conversation, isGroupPractice]);

    // GP: attach scroll listener when selected conversation changes
    React.useEffect(() => {
        if (!isGroupPractice || !gpSelectedConversationId) return;

        const messageWrap = document.getElementById('clientMessageWrap');
        const messageScrollCheck = (e: Event) => {
            const target = e.target as HTMLElement;
            const { scrollTop } = target;
            if (scrollTop === 0) {
                loadHistoryMessage();
            }
        };
        if (messageWrap) {
            messageWrap.addEventListener('scroll', messageScrollCheck);
        }
        return () => {
            if (messageWrap) {
                messageWrap.removeEventListener('scroll', messageScrollCheck);
            }
        };
    }, [isGroupPractice, gpSelectedConversationId]);

    const handleSelectGPConversation = async (cid: string) => {
        if (cid === gpSelectedConversationId) return;
        setGpSelectedConversationId(cid);
        selectConversationIdRef.current = cid;

        const cacheMessage = conversationMessageMap.current[cid] || [];
        setMessageList(cacheMessage);
        setReplayContentValue({ text: '', files: [] });

        const isMyConversation = cid.includes(`${getUserId()}`);
        if (isMyConversation) {
            await gpMarkRead(cid);
        }
        fetchGPConversations();
        fetchMessageByConversationId(cid);
    };

    React.useEffect(() => {
        if (!composerDraft) return;
        composerDraftActiveRef.current = true;
        const resolved = resolveComposerDraft(replayContentValue, composerDraft.text, autoSuggestionRef.current);
        setReplayContentValue(resolved.value);
        if (resolved.keptExisting) {
            message.info('Your unsent message was kept. Add the payment request if you still need it.');
        }
        onComposerDraftHandled?.();
    }, [composerDraft]);

    const onClickReply = async () => {
        if (!replayContentValue.text.trim() && replayContentValue.files.length === 0) {
            return;
        }
        const messageStr = replayContentValue.text.trim() ? replayContentValue.text.replace(/<[^>]+>/g, '') : '';
        const files = replayContentValue.files.map((file) => file.id);

        setReplyingMsg(true);
        try {
            if (messageStr) {
                if (isGroupPractice) {
                    const res = await gpSendMessage({
                        conversationId: selectConversationIdRef.current,
                        message: messageStr,
                    });
                    if (!res.error) {
                        setResetFlag({});
                        const messageData: TServerMessage = res.data?.data;
                        const cacheMessageList = conversationMessageMap.current[selectConversationIdRef.current!] || [];
                        const _message = unitAndSortServerMessages([...(cacheMessageList || []), messageData]);
                        conversationMessageMap.current[selectConversationIdRef.current!] = _message || [];
                        setMessageList(_message);
                        fetchGPConversations();
                    }
                } else {
                    const res = await sendMessage({
                        providerId: providerId!,
                        clientId: prospect?.id!,
                        message: messageStr,
                    });

                    if (!res.error) {
                        setResetFlag({});
                        const messageData: TServerMessage = res.data?.data;
                        const cacheMessageList = conversationMessageMap.current[selectConversationIdRef.current!] || [];
                        const _message = unitAndSortServerMessages([...(cacheMessageList || []), messageData]);
                        conversationMessageMap.current[selectConversationIdRef.current!] = _message || [];
                        setMessageList(_message);
                    }
                }
            }

            if (files.length > 0) {
                const sendFileFn = isGroupPractice
                    ? (fileId: number) => gpSendMessage({ conversationId: selectConversationIdRef.current, fileId })
                    : (fileId: number) => sendMessage({ providerId: providerId!, clientId: prospect?.id!, fileId });

                const res = await Promise.all(
                    replayContentValue.files.map(async (file) => {
                        if (!file.id) return null;
                        const resFile = await sendFileFn(file.id);
                        if (!resFile.error) {
                            return resFile.data.data;
                        }
                        return null;
                    }),
                );
                res.forEach((newMsg) => {
                    if (newMsg) {
                        const cacheMessageList = conversationMessageMap.current[selectConversationIdRef.current!] || [];
                        const _message = unitAndSortServerMessages([...(cacheMessageList || []), newMsg]);
                        conversationMessageMap.current[selectConversationIdRef.current!] = _message || [];
                        setMessageList(_message);
                    }
                });
                if (isGroupPractice) {
                    fetchGPConversations();
                }
            }

            setReplayContentValue({ text: '', files: [] });
            composerDraftActiveRef.current = false;
            scrollToLatestMessage(100, true);
        } finally {
            setReplyingMsg(false);
        }
    };

    const displayMessage = handleServerSideMessage(messageList, 0);

    const selectedConversation = isGroupPractice
        ? gpConversations.find((c) => c.conversationId === gpSelectedConversationId)
        : conversation;

    // GP: get display info for a conversation
    const getConversationDisplayInfo = (conv: TConversation) => {
        const otherMembers = conv.members?.filter((m) => m.userType !== EMessageSenderType.GP_USER) || [];
        const otherMember = otherMembers.length > 0 ? otherMembers[0] : conv.members?.[0];

        // For staff-patient conversations, find the staff member for display
        const staffMember = conv.members?.find(
            (m) => m.userType === EMessageSenderType.PROVIDER || m.userType === EMessageSenderType.GP_USER,
        );

        const displayName = otherMember
            ? `${otherMember.firstName || ''} ${otherMember.lastName || ''}`.trim() || otherMember.fullName || ''
            : '';

        const staffName = staffMember ? `${staffMember.firstName || ''} ${staffMember.lastName || ''}`.trim() : '';

        const providerMember = conv.members?.find((m) => m.userType === EMessageSenderType.PROVIDER);
        const providerFullName = providerMember
            ? `${providerMember.firstName || ''} ${providerMember.lastName || ''}`.trim()
            : '';

        return {
            displayName: staffName || displayName || 'Unknown',
            initials: getInitials(
                staffMember?.firstName || otherMember?.firstName,
                staffMember?.lastName || otherMember?.lastName,
            ),
            avatarColor: getAvatarColor(staffMember?.firstName || otherMember?.firstName || ''),
            avatar: staffMember?.avatar || otherMember?.avatar,
            isOnline: otherMember?.isOnline || false,
            roleCode: (staffMember as Record<string, unknown>)?.roleCode as string | undefined,
            providerName: providerFullName,
        };
    };

    const getLastMessageSenderName = (conv: TConversation) => {
        if (!conv.lastMessageSenderType) return '';
        const sender = conv.members?.find((m) => m.userType === conv.lastMessageSenderType);
        if (sender) {
            return `${sender.firstName || ''} ${sender.lastName || ''}`.trim();
        }
        return '';
    };

    // GP mode: conversation list + chat layout
    if (isGroupPractice) {
        return (
            <div className={`${s.gpWrap} ${hasBanner ? s.gpWrapWithBanner : ''}`}>
                <div className={s.gpLeft}>
                    <div className={s.gpLeftTitle}>Conversations</div>
                    <div className={s.gpList}>
                        {gpConversations.map((conv) => {
                            const info = getConversationDisplayInfo(conv);
                            const hasUnread = !!(conv.unreadCount && conv.unreadCount > 0);
                            const updateTime = dayjs(conv.lastMessageAt);
                            const isToday = updateTime.isSame(dayjs(), 'day');
                            const isSelected = gpSelectedConversationId === conv.conversationId;
                            const senderName = getLastMessageSenderName(conv);

                            return (
                                <div
                                    className={`${s.gpItem} ${hasUnread ? s.gpItemUnread : ''} ${isSelected ? s.gpItemSelect : ''}`}
                                    key={conv.conversationId}
                                    onClick={() => handleSelectGPConversation(conv.conversationId)}
                                >
                                    <div className={s.gpConvAvatarWrap}>
                                        {info.avatar ? (
                                            <img src={info.avatar} className={s.gpConvAvatar} />
                                        ) : (
                                            <div
                                                className={s.gpConvAvatarInitials}
                                                style={{ background: info.avatarColor }}
                                            >
                                                {info.initials}
                                            </div>
                                        )}
                                        {info.isOnline && <span className={s.gpConvOnlineDot} />}
                                    </div>
                                    <div className={s.gpConvBody}>
                                        <div className={s.gpConvNameRow}>
                                            <span className={s.gpConvName}>{info.displayName}</span>
                                            {hasUnread && <span className={s.gpUnreadCount}>{conv.unreadCount}</span>}
                                        </div>
                                        <div className={s.gpConvContent}>
                                            {senderName ? `${senderName}: ` : ''}
                                            {conv.lastMessageContent}
                                        </div>
                                        <div className={s.gpConvTimeRow}>
                                            <ClockCircleOutlined
                                                style={{ color: '#9CA3AF', marginRight: 4, fontSize: 12 }}
                                            />
                                            <span className={s.gpConvTime}>
                                                {isToday ? 'Today, ' : ''}
                                                {getTimeStr(dayjs(conv.lastMessageAt), 'MMM DD, hh:mm A')}
                                            </span>
                                        </div>
                                        {/* {info.providerName && (
                                            <div className={s.gpConvProviderTag}>from {info.providerName}</div>
                                        )} */}
                                    </div>
                                </div>
                            );
                        })}
                        {gpConversationsLoading && <Spin className={s.gpListLoading} />}
                        {!gpConversationsLoading && gpConversations.length === 0 && (
                            <div className={s.gpEmpty}>No conversations yet</div>
                        )}
                    </div>
                </div>
                <div className={s.gpMid}>
                    {gpSelectedConversationId ? (
                        <>
                            <div className={s.gpChatHeader}>
                                {(() => {
                                    const conv = gpConversations.find(
                                        (c) => c.conversationId === gpSelectedConversationId,
                                    );
                                    if (!conv) return null;
                                    const info = getConversationDisplayInfo(conv);
                                    return (
                                        <>
                                            <div className={s.gpChatHdrAvatar} style={{ background: info.avatarColor }}>
                                                {info.avatar ? (
                                                    <img src={info.avatar} className={s.gpChatHdrAvatarImg} />
                                                ) : (
                                                    <span>{info.initials}</span>
                                                )}
                                            </div>
                                            <span className={s.gpChatHdrName}>{info.displayName}</span>
                                        </>
                                    );
                                })()}
                            </div>
                            <div className={s.gpMessageWrap} id="clientMessageWrap">
                                {loadingHistoryMessageRef.current && <Spin className={s.messageLoadingHistory} />}
                                <InboxMessageList
                                    inboxMessageList={displayMessage}
                                    inboxConversation={selectedConversation}
                                />
                                {loadingConversationMessage && !loadingHistoryMessageRef.current && (
                                    <Spin className={s.messageLoading} />
                                )}
                            </div>
                            {!isGroupPractice && (
                                <div className={s.gpReplyWrap}>
                                    <ChatInput
                                        value={replayContentValue}
                                        onChange={(value) => {
                                            setReplayContentValue(value);
                                        }}
                                        onSend={onClickReply}
                                        placeholder="Enter your message"
                                        disabled={!canSend || isSample || replyingMsg}
                                        type={EAIEditorType.PROSPECT}
                                        clientId={prospect?.id || 0}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={s.gpEmptyChat}>
                            <div className={s.gpEmptyChatText}>Select a conversation to start messaging</div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Non-GP: original layout
    return (
        <div className={`${s.wrap} ${hasBanner ? s.wrapWithBanner : ''}`}>
            <div className={s.wrapper} id="clientMessageWrap">
                {loadingHistoryMessageRef.current && <Spin className={s.messageLoadingHistory} />}
                <InboxMessageList inboxMessageList={displayMessage} inboxConversation={conversation} />
                {loadingConversationMessage && !loadingHistoryMessageRef.current && (
                    <Spin className={s.messageLoading} />
                )}
            </div>

            <div className={s.replyWrap}>
                <ChatInput
                    value={replayContentValue}
                    onChange={(value) => {
                        setReplayContentValue(value);
                    }}
                    onSend={onClickReply}
                    placeholder="Enter your message"
                    disabled={!canSend || isSample || replyingMsg}
                    type={EAIEditorType.PROSPECT}
                    clientId={prospect?.id || 0}
                />
            </div>
        </div>
    );
};

export default ClientMessage;
